import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { HarmonizedViolationSeverity } from "generated-graphql/graphql";

import {
  formatViolationDate,
  isOldPermitSignal,
  lightFilledChipSx,
  MetricInfoIcon,
  OLD_SIGNAL_EXPLANATION,
  SEVERITY_COLOR,
  SEVERITY_EXPLANATION,
} from "./violationDisplay";

/** Display order for the three steps: lowest on the left, like a scale read left-to-right. */
const SEVERITY_STEPS = [
  HarmonizedViolationSeverity.Low,
  HarmonizedViolationSeverity.Medium,
  HarmonizedViolationSeverity.High,
] as const;

const STEP_LABEL: Record<HarmonizedViolationSeverity, string> = {
  [HarmonizedViolationSeverity.Low]: "Low",
  [HarmonizedViolationSeverity.Medium]: "Medium",
  [HarmonizedViolationSeverity.High]: "High",
};

/** The producer's own ISO date suffix, e.g. " (since 2015-03-01)". */
const ISO_SINCE_SUFFIX = /\s*\(since \d{4}-\d{2}-\d{2}\)$/;

/** Exactly water.ts's undated fallback shape — nothing looser, see below. */
const UNDATED_PERMIT_CLAUSE =
  /^Violation open against (?:an expired|a terminated|a retired) permit$/;

/**
 * The ranked "because" list: every applicable reason, in determinant rank
 * order, split out of the `; `-joined determinant description built by the
 * harmonize-violations stages (water.ts / waste.ts / air.ts).
 *
 * The producers already name the statute on the order clause and date the
 * permit clause in ISO ("(since 2015-03-01)"), so nothing is woven in here —
 * the permit clause's ISO date is only reformatted to the display style. When
 * the description is absent entirely, the structured columns fall back to
 * their own entries so a permit date or statute is never dropped on the floor.
 */
export function severityReasonClauses(input: {
  determinantDesc: string | null | undefined;
  orderStatuteCode: string | null | undefined;
  permitTerminalDate: string | null | undefined;
}): string[] {
  const { determinantDesc, orderStatuteCode, permitTerminalDate } = input;
  if (!determinantDesc) {
    const fallbacks: string[] = [];
    if (orderStatuteCode) {
      fallbacks.push(`Facility under an active ${orderStatuteCode} Order`);
    }
    if (permitTerminalDate) {
      fallbacks.push(
        `Violation open against an expired or terminated permit (since ${formatViolationDate(
          permitTerminalDate
        )})`
      );
    }
    return fallbacks;
  }
  let datedAClause = false;
  const clauses = determinantDesc
    .split("; ")
    .map((clause) => clause.trim())
    .filter((clause) => clause.length > 0)
    .map((clause) => {
      // Only the producer's own permit clause is re-dated. The trailing clause
      // can be EPA's raw free text (e.g. an RNC description like "Violation of
      // Permit Effluent Limit"), so a loose /permit/ match would staple a
      // fabricated date onto an unrelated sentence.
      if (permitTerminalDate && ISO_SINCE_SUFFIX.test(clause)) {
        datedAClause = true;
        // Replace, never append: doubling the date reads as a bug.
        return clause.replace(
          ISO_SINCE_SUFFIX,
          ` (since ${formatViolationDate(permitTerminalDate)})`
        );
      }
      if (permitTerminalDate && UNDATED_PERMIT_CLAUSE.test(clause)) {
        datedAClause = true;
        return `${clause} (since ${formatViolationDate(permitTerminalDate)})`;
      }
      return clause;
    });
  // The producers set permitTerminalDate and the permit clause under the same
  // SQL gate, so a set date with no matching clause shouldn't happen — but that
  // invariant lives in three SQL files. If it ever breaks, surface the date as
  // its own entry rather than dropping it (this replaced the standalone
  // "Permit status since" field).
  if (permitTerminalDate && !datedAClause) {
    clauses.push(
      `Violation open against an expired or terminated permit (since ${formatViolationDate(
        permitTerminalDate
      )})`
    );
  }
  return clauses;
}

function SeverityStep({ level, landed }: { level: HarmonizedViolationSeverity; landed: boolean }) {
  const color = SEVERITY_COLOR[level];
  return (
    <Chip
      size="small"
      label={STEP_LABEL[level]}
      variant={landed ? "filled" : "outlined"}
      sx={
        landed
          ? // The landed step is deliberately heavier than a grid SeverityChip:
            // same light fill, plus a border and bold label so it reads as the
            // selected stop among the three.
            {
              ...lightFilledChipSx(color),
              fontWeight: 700,
              border: 1,
              borderColor: `${color}.main`,
            }
          : { color: "text.disabled", borderColor: "divider" }
      }
    />
  );
}

/**
 * One compact block answering "why is this violation rated what it's rated" —
 * consolidating what used to be smeared across the severity chip's tooltip,
 * the escalation banners, the "Old signal" chip, and the "Permit status since"
 * detail field.
 *
 * Deliberately three discrete steps rather than a meter, gauge, or gradient:
 * severity is EPA's 3-bucket classification, not a harm score. A violation
 * matches a rule; it doesn't score — the closing disclaimer is load-bearing.
 */
export function SeverityProvenance({
  severity,
  determinantDesc,
  determinantCode,
  orderStatuteCode,
  permitTerminalDate,
  now,
}: {
  severity: HarmonizedViolationSeverity;
  determinantDesc: string | null | undefined;
  determinantCode: string | null | undefined;
  orderStatuteCode: string | null | undefined;
  permitTerminalDate: string | null | undefined;
  /** Injected so callers/tests stay deterministic (see isOldPermitSignal). */
  now: Date;
}) {
  const reasons = severityReasonClauses({ determinantDesc, orderStatuteCode, permitTerminalDate });
  // The dead-permit clause can still be present even when an active Order
  // outranks it — the "old signal" caution only applies when the permit clause
  // is actually the thing driving severity.
  const oldSignal =
    determinantCode === "PERMIT_EXPIRED" && isOldPermitSignal(permitTerminalDate, now);

  return (
    // The landed level is in the accessible name: visually it reads from the
    // filled step, but the accessibility tree would otherwise expose three
    // identical level chips with nothing marking the current one.
    <Box role="group" aria-label={`Severity: ${STEP_LABEL[severity]}`}>
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography variant="overline" color="text.primary" sx={{ fontWeight: 700 }}>
          Severity
        </Typography>
        <MetricInfoIcon explanation={SEVERITY_EXPLANATION} label="severity" />
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
        {SEVERITY_STEPS.map((level) => (
          <SeverityStep key={level} level={level} landed={level === severity} />
        ))}
      </Stack>
      {reasons.length > 0 ? (
        <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {reasons.map((reason) => (
            <Typography key={reason} component="li" variant="body2">
              {reason}
            </Typography>
          ))}
        </Box>
      ) : null}
      {reasons.length > 1 ? (
        // Deliberately NOT "any one alone is sufficient": the producers keep
        // non-escalating context clauses (e.g. "Handler has unresolved
        // violations") in the list, and those alone would land lower.
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          Several reasons apply; the first is what set this level.
        </Typography>
      ) : null}
      {oldSignal ? (
        <Typography variant="caption" color="warning.dark" component="p" sx={{ mt: 0.5 }}>
          {OLD_SIGNAL_EXPLANATION}
        </Typography>
      ) : null}
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mt: 0.5, fontStyle: "italic" }}
      >
        Severity ranks work — it does not measure harm.
      </Typography>
    </Box>
  );
}
